import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { TrendingDown, RefreshCw, MapPin, ExternalLink } from "lucide-react";

const TABS = [
  { key: "geral",   label: "Geral",   testid: "tab-devedores-geral" },
  { key: "lisboa",  label: "Lisboa",  testid: "tab-devedores-lisboa" },
  { key: "acores",  label: "Açores",  testid: "tab-devedores-acores" },
];

function medalClass(rank) {
  if (rank === 1) return "bg-yellow-400 text-yellow-900";
  if (rank === 2) return "bg-slate-300 text-slate-800";
  if (rank === 3) return "bg-amber-600 text-amber-100";
  return "bg-slate-100 text-slate-600";
}

const isAcores = (r) => /PDL/i.test(r.pasta || "");

function getActiveData(tab, all, lisboa, acores) {
  if (tab === "lisboa") return lisboa;
  if (tab === "acores") return acores;
  return all;
}

function tabSubtitle(tab) {
  if (tab === "lisboa") return "Escritório de Lisboa";
  if (tab === "acores") return "Escritório dos Açores";
  return "Total global em dívida";
}

export default function MaioresDevedores() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeTab, setActiveTab] = useState("geral");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/maiores-devedores`, { headers: getAuthHeader() });
      setData(res.data);
      setLastUpdate(new Date().toLocaleTimeString("pt-PT"));
    } catch (e) {
      setData([]); // garante estado limpo em caso de erro de rede
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const lisboaData = data.filter(r => !isAcores(r));
  const acoresData = data.filter(r => isAcores(r));
  const activeData = getActiveData(activeTab, data, lisboaData, acoresData);
  const activeTotal = activeData.reduce((s, r) => s + r.total, 0);
  const fmtEur = (v) => v.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingDown size={22} className="text-red-500" />
            Maiores Devedores
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Clientes com honorários por liquidar · Atualizado às {lastUpdate || "—"}
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="btn-refresh-devedores"
          className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Atualizar
        </button>
      </div>

      {/* Escritório tabs */}
      <div className="flex gap-2 bg-slate-100 rounded-xl p-1 w-fit">
        {TABS.map(({ key, label, testid }) => (
          <button key={key} data-testid={testid}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === key ? "bg-[#0b679e] text-white shadow-sm" : "text-slate-600 hover:bg-slate-200"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total em Dívida</p>
          <p className="text-2xl font-bold text-red-600" data-testid="total-divida-global">
            {loading ? "—" : fmtEur(activeTotal)}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {tabSubtitle(activeTab)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Maior Devedor</p>
          <p className="text-lg font-bold text-slate-800 truncate" data-testid="maior-devedor-nome">
            {loading || !activeData[0] ? "—" : activeData[0].nome_cliente}
          </p>
          <p className="text-sm text-red-500 font-semibold">
            {loading || !activeData[0] ? "" : fmtEur(activeData[0].total)}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Processos com Dívida</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="total-processos-divida">
            {loading ? "—" : activeData.length}
          </p>
          <p className="text-xs text-slate-400 mt-1">Com honorários por pagar</p>
        </div>
      </div>

      {/* Ranking table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {(() => {
          if (loading) return (
            <div className="flex items-center justify-center h-48">
              <div className="w-8 h-8 border-4 border-[#0b679e] border-t-[#F5A623] rounded-full animate-spin" />
            </div>
          );
          if (activeData.length === 0) return (
            <div className="text-center py-16 text-slate-400">
              <TrendingDown size={40} className="mx-auto mb-3 opacity-30" />
              <p>Nenhum devedor encontrado</p>
            </div>
          );
          return (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-14">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Nº Processo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Situação</th>
                  {activeTab === "geral" && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Escritório</th>
                  )}
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Total em Dívida</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeData.map((r, i) => (
                  <tr key={r.processo_id}
                    data-testid={`devedor-row-${i + 1}`}
                    className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${medalClass(i + 1)}`}>
                        {i + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 max-w-xs truncate">{r.nome_cliente}</p>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-[#0b679e] font-semibold">{r.numero_processo}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                        {r.situacao}
                      </span>
                    </td>
                    {activeTab === "geral" && (
                      <td className="px-4 py-3">
                        {isAcores(r) ? (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-teal-100 text-teal-700 border border-teal-200">
                            <MapPin size={8} /> Açores
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500">Lisboa</span>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold text-red-600 text-sm">{fmtEur(r.total)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => navigate(`/processos/${r.processo_id}`)}
                        data-testid={`btn-ver-processo-${i + 1}`}
                        title="Abrir processo"
                        className="text-slate-400 hover:text-[#0b679e] transition-colors">
                        <ExternalLink size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                <tr>
                  <td colSpan={activeTab === "geral" ? 5 : 4} className="px-4 py-3 text-sm font-bold text-slate-700">
                    Total em Dívida ({activeData.length} processos)
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-600 text-sm">{fmtEur(activeTotal)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          );
        })()}
      </div>
    </div>
  );
}
